import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useState } from "react";
import { IconButton, List, Drawer, Card } from "@material-tailwind/react";
import { useAuth } from "@/contexts/auth/AuthContext";
import Analytics from "@/lib/analytics";
import AccountIcon from "./icons/Account";
import Hamburger from "./icons/Hamburger";
import XIcon from "./icons/XIcon";
import XQIcon from "./icons/XQIcon";
import NavUserSection from "./NavUserSection";

const Search = dynamic(() => import("./Search"), {
  ssr: false,
});

export default function Navbar() {
  const { user, logOut, openLoginPrompt } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const pathname = usePathname() || "";

  const openDrawer = () => {
    setIsDrawerOpen(true);
    drawerRef?.current?.focus();
  };
  const closeDrawer = () => setIsDrawerOpen(false);

  const handleNavLinkClick = (clickedLink: string) => {
    if (isDrawerOpen) closeDrawer();
    Analytics.handleNavLinkClick(clickedLink);
  };

  return (
    <nav
      className="sticky top-0 z-[1000] w-full border-b border-tab-border bg-white print:hidden"
      aria-label="Main menu navigation"
      role="navigation"
    >
      <div className="flex h-[72px] max-w-screen-large items-center justify-between px-4 py-5 text-xl medium:h-[80px] medium:px-6 large:mx-auto">
        <div className="flex items-center">
          <h1
            aria-label="XQ Competency Navigator"
            className="m-0 flex items-center p-0"
          >
            <Link
              href="/home"
              shallow={true}
              aria-label="Explore the navigator from the beginning"
              className="mr-3 flex"
            >
              <XQIcon />
              <div className="ml-4 font-roobert text-base font-medium leading-none text-neutral-800">
                Competency
                <br />
                Navigator
              </div>
            </Link>
          </h1>
        </div>
        <div className="flex items-center gap-4 medium:gap-10">
          <div className="hidden h-[72px] items-center gap-10 self-center border-b-[4px] border-transparent font-roobert text-base font-semibold leading-tight text-neutral-800 medium:flex medium:h-[80px]">
            <Link
              href="/home"
              shallow={true}
              aria-label="Home"
              className={`h-[72px] content-center hover:text-BG-55 medium:h-[80px] ${
                pathname === "/home"
                  ? "self-stretch border-b-[4px] border-b-BG-55 text-BG-55"
                  : ""
              }`}
              onClick={() => handleNavLinkClick("home")}
            >
              Home
            </Link>
            <Link
              href="/"
              shallow={true}
              aria-label="Competencies"
              className={`h-[72px] content-center hover:text-BG-55 medium:h-[80px] ${
                pathname === "/"
                  ? "self-stretch border-b-[4px] border-b-BG-55 text-BG-55"
                  : ""
              }`}
              onClick={() => handleNavLinkClick("competencies")}
            >
              XQ Competencies
            </Link>

            <Link
              href="/activities"
              shallow={true}
              aria-label="Activities"
              className={`h-[72px] content-center hover:text-BG-55 medium:h-[80px] ${
                pathname.match(/^\/activities/)
                  ? "self-stretch border-b-[4px] border-b-BG-55 text-BG-55"
                  : ""
              }`}
              onClick={() => handleNavLinkClick("activities")}
            >
              Activities
            </Link>
            <Link
              href="/about"
              shallow={true}
              aria-label="about"
              className={`h-[72px] content-center hover:text-BG-55 medium:h-[80px] ${
                pathname === "/about"
                  ? "self-stretch border-b-[4px] border-b-BG-55 text-BG-55"
                  : ""
              }`}
              onClick={() => handleNavLinkClick("about")}
            >
              About
            </Link>
          </div>

          <div className="flex items-center gap-4 medium:gap-6">
            <Search />

            <IconButton
              onClick={openDrawer}
              className="w-6 p-0 shadow-none medium:hidden"
              aria-label="hamburger menu"
            >
              <Hamburger className="flex" aria-label="hamburger menu icon" />
            </IconButton>

            {!user && (
              <IconButton
                className="shadow-none medium:hidden"
                onClick={() => openLoginPrompt("nav_bar")}
                aria-label="account icon"
              >
                <AccountIcon />
              </IconButton>
            )}
            <NavUserSection
              user={user}
              handleLogout={logOut}
              handleLogin={() => openLoginPrompt("nav_bar")}
            />
          </div>

          {/* Small Screen Nav View */}

          <Drawer
            open={isDrawerOpen}
            onClose={closeDrawer}
            className="z-10000 p-0 medium:hidden"
            placement="right"
            overlayProps={{
              className:
                "z-10000 bg-opacity-50 medium:hidden w-screen h-screen",
            }}
            id="mobile-nav"
            ref={drawerRef}
          >
            {isDrawerOpen && (
              <Card
                color="transparent"
                shadow={false}
                className="h-[calc(100vh-2rem)] w-full"
              >
                <List className="gap-0 p-0">
                  <div className="ml-5 mr-4 flex h-[72px] items-center justify-end border-b border-b-tab-border">
                    <button
                      onClick={closeDrawer}
                      className="rounded-full p-1 hover:bg-black/5 active:bg-black/10"
                      aria-label="Close Nav Drawer"
                    >
                      <XIcon className="h-5 w-5 stroke-2" />
                    </button>
                  </div>
                  <div
                    className={`flex h-[55px] items-center hover:text-BG-55 ${
                      pathname === "/home"
                        ? "border-l-[4px] border-l-BG-55 text-BG-55"
                        : ""
                    }`}
                  >
                    <Link
                      href="/home"
                      shallow={true}
                      className="h-full w-full py-4 font-normal hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      onClick={() => handleNavLinkClick("home")}
                    >
                      <span
                        className={`${pathname === "/home" ? "pl-5" : "pl-6"}`}
                      >
                        Home
                      </span>
                    </Link>
                  </div>
                  <hr className="ml-5 mr-4 border-tab-border" />
                  <div
                    className={`flex h-[55px] items-center hover:text-BG-55 ${
                      pathname === "/"
                        ? "border-l-[4px] border-l-BG-55 text-BG-55"
                        : ""
                    }`}
                  >
                    <Link
                      href="/"
                      shallow={true}
                      className="h-full w-full py-4 font-normal hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      onClick={() => handleNavLinkClick("competencies")}
                    >
                      <span className={`${pathname === "/" ? "pl-5" : "pl-6"}`}>
                        XQ Competencies
                      </span>
                    </Link>
                  </div>
                  <hr className="ml-5 mr-4 border-tab-border" />
                  <div
                    className={`flex h-[55px] items-center hover:text-BG-55 ${
                      pathname.match(/^\/activities/)
                        ? "border-l-[4px] border-l-BG-55 text-BG-55"
                        : ""
                    }`}
                  >
                    <Link
                      href="/activities"
                      shallow={true}
                      className="h-full w-full py-4 font-normal hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      onClick={() => handleNavLinkClick("activities")}
                    >
                      <span
                        className={`${
                          pathname.match(/^\/activities/) ? "pl-5" : "pl-6"
                        }`}
                      >
                        Activities
                      </span>
                    </Link>
                  </div>
                  <hr className="ml-5 mr-4 border-tab-border" />
                  <div
                    className={`flex h-[55px] items-center hover:text-BG-55 ${
                      pathname === "/about"
                        ? "border-l-[4px] border-l-BG-55 text-BG-55"
                        : ""
                    }`}
                  >
                    <Link
                      href="/about"
                      shallow={true}
                      className="h-full w-full py-4 font-normal hover:bg-tab-bg-hover active:bg-tab-bg-press"
                      onClick={() => handleNavLinkClick("about")}
                    >
                      <span
                        className={`${pathname === "/about" ? "pl-5" : "pl-6"}`}
                      >
                        About
                      </span>
                    </Link>
                  </div>
                  <hr className="ml-5 mr-4 border-tab-border" />
                </List>
              </Card>
            )}
          </Drawer>
        </div>
      </div>
    </nav>
  );
}
